import { useState } from "react";
import { Gift, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { api, formatCHF } from "@/lib/api";
import { useSeo, breadcrumb } from "@/lib/seo";
import { useLocale } from "@/context/LocaleContext";

const MONTANTS = [25, 50, 100, 150, 200, 250, 500];
const inputCls = "w-full bg-white/60 border border-wood/25 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-terra";

const GiftCard = () => {
  const { locale, t } = useLocale();
  const [montant, setMontant] = useState(100);
  const [autre, setAutre] = useState("");
  const [quantite, setQuantite] = useState(1);
  const [form, setForm] = useState({ acheteur_nom: "", acheteur_email: "", destinataire_nom: "", destinataire_email: "", message: "" });
  const [loading, setLoading] = useState(false);

  useSeo({
    title: locale === "en" ? "Gift card — Give a drum, workshop or circle · Entre Les Mondes" : "Carte cadeau — Offrir un tambour, un stage ou un cercle · Entre les Mondes",
    description: locale === "en" ? "Give an Entre Les Mondes gift card, valid for drums, drum-making workshops, circles, care and consultations. Sent by email, with a flexible amount from CHF 25 to CHF 2,000." : "Offrez une carte cadeau Entre Les Mondes, valable sur les tambours, les stages de création, les cercles, les soins et les consultations. Envoi par courriel, montant libre de CHF 25.– à 2000.–.",
    jsonLd: [breadcrumb([["Accueil", "/"], ["Carte cadeau", "/carte-cadeau"]]), { "@type": "Product", name: "Carte cadeau Entre Les Mondes", description: "Carte cadeau à montant libre, valable un an sur toute la boutique et les prestations.", brand: { "@type": "Brand", name: "Entre Les Mondes" }, offers: { "@type": "AggregateOffer", lowPrice: 25, highPrice: 2000, priceCurrency: "CHF", availability: "https://schema.org/InStock" } }],
  });

  const valeur = autre !== "" ? Number(autre) : montant;
  const valide = valeur >= 25 && valeur <= 2000;
  const total = valide ? valeur * quantite : 0;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!valide) { toast.error(t("Montant entre CHF 25.– et CHF 2000.–")); return; }
    setLoading(true);
    try {
      const payload = { montant: valeur, quantite, origin_url: window.location.origin, ...form };
      if (!payload.destinataire_email) delete payload.destinataire_email;
      if (!payload.destinataire_nom) delete payload.destinataire_nom;
      const res = await api.buyGiftcard(payload);
      window.location.assign(res.checkout_url);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("La commande n'a pas abouti, merci de réessayer."));
      setLoading(false);
    }
  };

  return (
    <div>
      <section className="bg-earth grain text-sand relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-44 pb-20 relative z-[2]">
          <Reveal>
            <p className="eyebrow mb-6 text-sand/70">{t("Carte cadeau")}</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-3xl">
              {t("Offrir un battement, un tambour, une journée.")}
            </h1>
            <p className="mt-6 max-w-2xl text-sand/70 leading-relaxed">
              {t("Une carte cadeau Entre Les Mondes se dépense librement : stage de création, création individuelle, formation ou tambour de la boutique. Envoyée par courriel, valable un an.")}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-start">
        <Reveal className="lg:col-span-5 lg:sticky lg:top-28">
          <div className="relative aspect-[1.6/1] rounded-2xl bg-earth grain text-sand p-8 flex flex-col justify-between overflow-hidden shadow-2xl shadow-earth/30" data-testid="giftcard-preview">
            <div className="relative z-[2] flex items-start justify-between">
              <img src="/logo.png" alt="" className="w-12 h-12 object-contain" />
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-sand/60">{t("Carte cadeau")}</span>
            </div>
            <div className="relative z-[2]">
              <p className="font-serif text-5xl" data-testid="giftcard-preview-montant">{valide ? formatCHF(valeur) : "—"}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-sand/60 mt-3">{t("Entre Les Mondes · Orbe · valable un an")}</p>
            </div>
            <span className="absolute -right-10 -bottom-16 font-serif text-[14rem] leading-none text-sand/5 select-none">ELM</span>
          </div>
          <div className="mt-8 space-y-4 text-sm text-fumee leading-relaxed">
            <p className="flex gap-3"><Gift className="w-4 h-4 text-terra shrink-0 mt-0.5" /> {t("Le code cadeau arrive par courriel dès le paiement confirmé — à vous, et au destinataire si vous indiquez son adresse.")}</p>
            <p className="flex gap-3"><ArrowRight className="w-4 h-4 text-terra shrink-0 mt-0.5" /> {t("Il s'utilise en une ou plusieurs fois, à la boutique comme sur les stages, en saisissant le code au moment de réserver.")}</p>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-7">
          <form onSubmit={submit} className="bg-card border border-sand-dark rounded-2xl p-8 lg:p-10 space-y-8" data-testid="giftcard-form">
            <div>
              <span className="eyebrow block mb-3">{t("Montant")}</span>
              <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t("Montant de la carte cadeau")}>
                {MONTANTS.map((m) => (
                  <button type="button" key={m} data-testid={`giftcard-montant-${m}`} onClick={() => { setMontant(m); setAutre(""); }}
                    className={`px-5 py-3 rounded-full border text-sm font-mono transition-colors duration-300 ${
                      autre === "" && montant === m ? "border-terra bg-terra/10 text-terra" : "border-wood/25 text-fumee hover:border-wood/50"
                    }`}>
                    CHF {m}
                  </button>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-3">
                <label htmlFor="gc-autre" className="text-sm text-fumee shrink-0">{t("Autre montant")}</label>
                <input id="gc-autre" data-testid="giftcard-autre" type="number" min={25} max={2000} step={5} value={autre} onChange={(e) => setAutre(e.target.value)} placeholder="CHF 25 – 2000" className={`${inputCls} max-w-[200px]`} />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="gc-qte" className="eyebrow block mb-2">{t("Quantité")}</label>
                <select id="gc-qte" data-testid="giftcard-quantite" value={quantite} onChange={(e) => setQuantite(Number(e.target.value))} className={inputCls}>
                  {[1, 2, 3, 4, 5].map((q) => <option key={q} value={q}>{q}</option>)}
                </select>
              </div>
            </div>
            <div className="border-t border-sand-dark pt-6 grid sm:grid-cols-2 gap-5">
              <div className="sm:col-span-2"><p className="font-serif text-xl text-bark">{t("De la part de")}</p></div>
              <div>
                <label htmlFor="gc-nom" className="eyebrow block mb-2">{t("Votre nom")}</label>
                <input id="gc-nom" data-testid="giftcard-acheteur-nom" required value={form.acheteur_nom} onChange={set("acheteur_nom")} className={inputCls} />
              </div>
              <div>
                <label htmlFor="gc-email" className="eyebrow block mb-2">{t("Votre courriel")}</label>
                <input id="gc-email" data-testid="giftcard-acheteur-email" type="email" required value={form.acheteur_email} onChange={set("acheteur_email")} className={inputCls} />
              </div>
            </div>
            <div className="border-t border-sand-dark pt-6 grid sm:grid-cols-2 gap-5">
              <div className="sm:col-span-2">
                <p className="font-serif text-xl text-bark">{t("Pour")}</p>
                <p className="text-xs text-fumee mt-1">{t("Optionnel — si vous préférez remettre la carte vous-même, laissez vide : vous recevrez le code par courriel.")}</p>
              </div>
              <div>
                <label htmlFor="gc-dnom" className="eyebrow block mb-2">{t("Nom du destinataire")}</label>
                <input id="gc-dnom" data-testid="giftcard-destinataire-nom" value={form.destinataire_nom} onChange={set("destinataire_nom")} className={inputCls} />
              </div>
              <div>
                <label htmlFor="gc-demail" className="eyebrow block mb-2">{t("Courriel du destinataire")}</label>
                <input id="gc-demail" data-testid="giftcard-destinataire-email" type="email" value={form.destinataire_email} onChange={set("destinataire_email")} className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="gc-msg" className="eyebrow block mb-2">{t("Votre message")}</label>
                <textarea id="gc-msg" data-testid="giftcard-message" rows={3} value={form.message} onChange={set("message")} placeholder={t("Quelques mots qui accompagneront la carte…")} className={`${inputCls} resize-none`} />
              </div>
            </div>
            <div className="flex items-center justify-between pt-2">
              <div>
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-fumee block">{t("Total")}</span>
                <span className="font-serif text-3xl text-bark" data-testid="giftcard-total">{formatCHF(total)}</span>
              </div>
              <p className="text-xs text-fumee mb-4 flex items-center gap-2" data-testid="giftcard-paiement-info">{t("Paiement en ligne immédiat par TWINT ou carte — sécurisé par Stripe.")}</p>
              <button type="submit" data-testid="giftcard-submit" disabled={loading || !valide}
                className="px-8 py-4 rounded-full bg-terra text-earth text-sm tracking-wide hover:bg-earth transition-colors duration-300 disabled:opacity-50 inline-flex items-center gap-2">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {loading ? t("Redirection…") : t("Offrir et payer (TWINT / carte)")}
              </button>
            </div>
          </form>
        </Reveal>
      </section>
    </div>
  );
};

export default GiftCard;
